import styles from './Ticket.module.scss';

import qs from 'query-string';
import { useRef } from 'react';
import classNames from 'classnames';
import { RxDragHandleDots2 } from 'react-icons/rx';

import {
  EventAnalyticsTicketScansByTierDataPointDto,
  EventTierCreation
} from 'legacy/events/types';

import Toggle from 'legacy/common/components/form/Toggle';
import { currencyFormat } from 'legacy/common/utils/currency';
import { MdContentCopy, MdLockOpen } from 'react-icons/md';

import { copyToClipboard } from 'legacy/common/utils/clipboard';
import * as toast from 'legacy/common/utils/toast';

import config from 'config';
import TicketStats from './TicketStats';
import { OutgoingEventProductDto } from 'api/dtos/events/products';

type TicketProps = {
  ticket: OutgoingEventProductDto;
  testId?: string;
  dataTestId?: string;
  eventId?: string;
  eventShareToken?: string;
  onClick: () => void;
  toggleTicketTier: (isDisabled: boolean) => void;
  isSortable?: boolean;
  toggleHideTier: () => void;
  lastCreatedTicketId?: string;
  tierScansData?: EventAnalyticsTicketScansByTierDataPointDto;
};

const Ticket: React.FC<TicketProps> = ({
  eventId,
  eventShareToken,
  ticket,
  testId,
  onClick,
  toggleTicketTier,
  isSortable,
  toggleHideTier,
  lastCreatedTicketId,
  tierScansData
}) => {
  const ticketRef = useRef<HTMLDivElement>();

  const params = qs.stringify({
    tierShareToken: ticket.shareToken,
    invite: eventShareToken
  });
  const shareUrl = `${config.SITE_URL}events/${eventId}?${params}`;

  const ticketId = (ticket as EventTierCreation).id;

  return (
    <div onClick={onClick} data-testid={testId} ref={ticketRef}>
      <div
        className={classNames('relative w-full max-w-[550px]', {
          'opacity-60': !ticket.isActive
        })}
      >
        <div
          className={classNames(
            'w-full flex justify-between relative cursor-pointer border-1 rounded-2xl p-4 border-gray-200 hover:border-gray-500',
            {
              [styles.tierAnimation]: lastCreatedTicketId === ticketId
            }
          )}
        >
          {isSortable && (
            <div className="absolute top-5 right-4">
              <RxDragHandleDots2
                size={25}
                className="cursor-grab text-[#787878]"
              />
            </div>
          )}

          <div className="w-full">
            <div className="flex items-center w-[85%]">
              <div>
                <div className="flex items-start font-normal text-base text-[#202020]">
                  {ticket.isPrivate && (
                    <span data-testid={`${testId}-lock-icon`}>
                      <MdLockOpen size={20} className="mr-1" />
                    </span>
                  )}

                  <span data-testid={`${testId}-name`}>{ticket.name}</span>
                </div>

                <div className="text-base font-bold text-[#202020]">
                  {currencyFormat(ticket.price)}
                </div>
              </div>
            </div>
            {ticket.isPrivate && (
              <div className="flex mt-4 font-semibold text-gray-500">
                <div
                  className="mr-2 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(ticket.shareToken);

                    toast.showToast({
                      type: 'success',
                      message: 'Private code copied to clipboard'
                    });
                  }}
                >
                  <span>Private code: {ticket.shareToken}</span>
                  <div className="px-2">
                    <MdContentCopy size={16} className="text-primary-600" />
                  </div>
                </div>

                <div
                  className="flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();

                    copyToClipboard(shareUrl);
                    toast.showToast({
                      type: 'success',
                      message: 'Unlock URL copied to clipboard'
                    });
                  }}
                >
                  Unlock URL
                  <div className="px-2">
                    <MdContentCopy size={16} className="text-primary-600" />
                  </div>
                </div>
              </div>
            )}
            {ticket.description && (
              <div
                data-testid={`${testId}-description`}
                className="text-[#575757] text-base font-normal mt-4"
              >
                {ticket.description}
              </div>
            )}
            <div className="mt-4 w-full border-t border-[#EBEBEB]" />

            <TicketStats
              tierScansData={tierScansData}
              purchases={ticket.purchases}
              maxAttendance={ticket.maxQuantity}
              requiresApproval={ticket.purchaseRequirements?.requiresApproval}
              questions={ticket.purchaseRequirements?.questions}
              testId={testId}
            />

            <div className="mt-4 w-full border-t border-[#EBEBEB]" />

            <div className="mt-4 flex flex-col gap-3">
              <div className="flex flex-row justify-between ">
                <div className="flex gap-3">
                  <svg
                    width="12"
                    height="14"
                    viewBox="0 0 12 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M0 14H4V0H0V14ZM8 0V14H12V0H8Z" fill="#202020" />
                  </svg>
                  <span className="text-sm text-[#202020]">
                    Pause tier sales
                  </span>
                </div>

                <div className="ml-2">
                  <Toggle
                    isActive={!ticket?.isActive}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!ticket?.isActive) {
                        toggleTicketTier(false);
                        return;
                      }

                      toggleTicketTier(true);
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-row items-center justify-between">
                <div className="flex -ml-1 gap-2">
                  <svg
                    width="22"
                    height="20"
                    viewBox="0 0 22 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11 3.97505C14.79 3.97505 18.17 6.10505 19.82 9.47505C19.23 10.695 18.4 11.745 17.41 12.595L18.82 14.005C20.21 12.775 21.31 11.235 22 9.47505C20.27 5.08505 16 1.97505 11 1.97505C9.73 1.97505 8.51 2.17505 7.36 2.54505L9.01 4.19505C9.66 4.06505 10.32 3.97505 11 3.97505ZM9.93 5.11505L12 7.18505C12.57 7.43505 13.03 7.89505 13.28 8.46505L15.35 10.535C15.43 10.195 15.49 9.83505 15.49 9.46505C15.5 6.98505 13.48 4.97505 11 4.97505C10.63 4.97505 10.28 5.02505 9.93 5.11505ZM1.01 1.84505L3.69 4.52505C2.06 5.80505 0.77 7.50505 0 9.47505C1.73 13.865 6 16.975 11 16.975C12.52 16.975 13.98 16.685 15.32 16.155L18.74 19.575L20.15 18.165L2.42 0.425049L1.01 1.84505ZM8.51 9.34505L11.12 11.955C11.08 11.965 11.04 11.975 11 11.975C9.62 11.975 8.5 10.855 8.5 9.47505C8.5 9.42505 8.51 9.39505 8.51 9.34505ZM5.11 5.94505L6.86 7.69505C6.63 8.24505 6.5 8.84505 6.5 9.47505C6.5 11.955 8.52 13.975 11 13.975C11.63 13.975 12.23 13.845 12.77 13.615L13.75 14.595C12.87 14.835 11.95 14.975 11 14.975C7.21 14.975 3.83 12.845 2.18 9.47505C2.88 8.04505 3.9 6.86505 5.11 5.94505Z"
                      fill="#202020"
                    />
                  </svg>
                  <span className="text-sm text-[#202020]">
                    Hide tier from purchasers
                  </span>
                </div>
                <div className="ml-2">
                  <Toggle
                    isActive={!ticket?.isVisible}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleHideTier();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ticket;
